import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../animations/variants';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Switch,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  Divider,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { listCloudTrailAccounts, createTrail, getTrail } from '../../services/cloudtrailApi';
import { getCloudTrailUser } from '../../services/cloudtrailApi';

const steps = ['Trail Settings', 'Select Accounts', 'S3 Bucket', 'Events'];

const dataResourceTypes = [
  'AWS::S3::Object',
  'AWS::Lambda::Function',
  'AWS::DynamoDB::Table',
];

const networkEventSources = [
  'aco-automation.amazonaws.com',
  'appconfig.amazonaws.com',
  'application-signals.amazonaws.com',
  'appmesh.amazonaws.com',
  'athena.amazonaws.com',
  'b2bi.amazonaws.com',
  'backup-gateway.amazonaws.com',
  'bcm-data-exports.amazonaws.com',
  'bcm-pricing-calculator.amazonaws.com',
  'bedrock.amazonaws.com',
  'billing.amazonaws.com',
  'cassandra.amazonaws.com',
  'ce.amazonaws.com',
  'cloudcontrolapi.amazonaws.com',
  'cloudformation.amazonaws.com',
  'cloudhsm.amazonaws.com',
  'cloudtrail.amazonaws.com',
  'codedeploy.amazonaws.com',
  'comprehend.amazonaws.com',
  'comprehendmedical.amazonaws.com',
  'config.amazonaws.com',
  'ds.amazonaws.com',
  'dynamodb.amazonaws.com',
  'ec2.amazonaws.com',
  'ecs.amazonaws.com',
  'elasticfilesystem.amazonaws.com',
  'elasticloadbalancing.amazonaws.com',
  'events.amazonaws.com',
  'firehose.amazonaws.com',
  'frauddetector.amazonaws.com',
  'freetier.amazonaws.com',
  'fsx.amazonaws.com',
  'glue.amazonaws.com',
  'healthlake.amazonaws.com',
  'invoicing.amazonaws.com',
  'iot.amazonaws.com',
  'iotfleetwise.amazonaws.com',
  'iotsecuredtunneling.amazonaws.com',
  'kms.amazonaws.com',
  'lakeformation.amazonaws.com',
  'lambda.amazonaws.com',
  'license-manager.amazonaws.com',
  'lookoutequipment.amazonaws.com',
  'lookoutvision.amazonaws.com',
  'monitoring.amazonaws.com',
  'personalize.amazonaws.com',
  'qbusiness.amazonaws.com',
  'rds.amazonaws.com',
  'rekognition.amazonaws.com',
  'rolesanywhere.amazonaws.com',
  's3.amazonaws.com',
  'sagemaker.amazonaws.com',
  'scheduler.amazonaws.com',
  'secretsmanager.amazonaws.com',
  'servicediscovery.amazonaws.com',
  'sns.amazonaws.com',
  'sqs.amazonaws.com',
  'ssm-contacts.amazonaws.com',
  'ssm.amazonaws.com',
  'storagegateway.amazonaws.com',
  'swf.amazonaws.com',
  'textract.amazonaws.com',
  'transcribe.amazonaws.com',
  'transcribestreaming.amazonaws.com',
  'transform.amazonaws.com',
  'translate.amazonaws.com',
  'user-subscriptions.amazonaws.com',
  'verifiedpermissions.amazonaws.com',
  'voiceid.amazonaws.com',
  'workmail.amazonaws.com',
  'workmailmessageflow.amazonaws.com',
];

const CreateTrail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cloneFromId = searchParams.get('cloneFrom');
  const [activeStep, setActiveStep] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Step 1: Trail Settings
  const [trailName, setTrailName] = useState('');
  const [isMultiRegion, setIsMultiRegion] = useState(true);
  const [region, setRegion] = useState('us-east-1');

  // Step 2: Select Accounts
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState('');

  // Step 3: S3 Bucket
  const [s3BucketAccountId, setS3BucketAccountId] = useState('');
  const [s3BucketOption, setS3BucketOption] = useState('new');
  const [s3BucketName, setS3BucketName] = useState('');
  const [s3Prefix, setS3Prefix] = useState('');

  // Step 4: Events
  const [mgmtEnabled, setMgmtEnabled] = useState(true);
  const [mgmtReadWriteType, setMgmtReadWriteType] = useState('All');
  const [excludeKMS, setExcludeKMS] = useState(false);
  const [excludeRDSData, setExcludeRDSData] = useState(false);
  const [dataEvents, setDataEvents] = useState([]);
  const [insightApiCall, setInsightApiCall] = useState(false);
  const [insightApiError, setInsightApiError] = useState(false);
  const [networkEvents, setNetworkEvents] = useState([]);

  const user = getCloudTrailUser();

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listCloudTrailAccounts();
      setAccounts(response.data?.accounts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Auto-suggest bucket name when realm is available
  useEffect(() => {
    if (s3BucketOption === 'new' && !s3BucketName && user?.realm) {
      const ts = Math.floor(Date.now() / 1000);
      setS3BucketName(`cloudtrail-${user.realm}-${ts}`);
    }
  }, [s3BucketOption, s3BucketName, user?.realm]);

  // Pre-populate from source trail when cloning
  useEffect(() => {
    if (!cloneFromId || loading) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await getTrail(cloneFromId);
        if (cancelled) return;
        const source = response.data;
        if (!source) return;
        // Pre-populate settings (NOT name, accounts, or bucket)
        setRegion(source.region || 'us-east-1');
        setIsMultiRegion(source.isMultiRegion ?? true);
        setS3Prefix(source.s3Prefix || '');
        setS3BucketOption('new');
        // Pre-populate event config
        const ec = source.eventConfig;
        if (ec) {
          if (ec.managementEvents) {
            setMgmtEnabled(ec.managementEvents.enabled ?? true);
            setMgmtReadWriteType(ec.managementEvents.readWriteType || 'All');
            setExcludeKMS(ec.managementEvents.excludeKMS ?? false);
            setExcludeRDSData(ec.managementEvents.excludeRDSData ?? false);
          }
          if (ec.dataEvents?.length > 0) {
            setDataEvents(
              ec.dataEvents.map((de) => ({
                resourceType: de.resourceType,
                readWriteType: de.readWriteType,
                resourceArns: de.resourceArns?.join(', ') || '',
              }))
            );
          }
          if (ec.insightSelectors?.length > 0) {
            setInsightApiCall(ec.insightSelectors.some((s) => s.insightType === 'ApiCallRateInsight'));
            setInsightApiError(ec.insightSelectors.some((s) => s.insightType === 'ApiErrorRateInsight'));
          }
          if (ec.networkActivityEvents?.length > 0) {
            const legacyMap = {
              'AWS::EC2::VPC': 'ec2.amazonaws.com',
              'AWS::EC2::Subnet': 'ec2.amazonaws.com',
              'AWS::EC2::NetworkInterface': 'ec2.amazonaws.com',
            };
            setNetworkEvents(
              ec.networkActivityEvents.map((ne) => ({
                eventSource: ne.eventSource || legacyMap[ne.resourceType] || '',
              }))
            );
          }
        }
      } catch (err) {
        console.error('Failed to load source trail for cloning:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [cloneFromId, loading]);

  const handleSelectAll = () => {
    if (selectedAccounts.length === accounts.length) {
      setSelectedAccounts([]);
    } else {
      setSelectedAccounts(accounts.map((a) => a.accountId));
    }
  };

  const handleToggleAccount = (accountId) => {
    setSelectedAccounts((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId]
    );
  };

  const addDataEvent = () => {
    setDataEvents([...dataEvents, { resourceType: 'AWS::S3::Object', readWriteType: 'All', resourceArns: '' }]);
  };

  const removeDataEvent = (index) => {
    setDataEvents(dataEvents.filter((_, i) => i !== index));
  };

  const updateDataEvent = (index, field, value) => {
    const updated = [...dataEvents];
    updated[index] = { ...updated[index], [field]: value };
    setDataEvents(updated);
  };

  const addNetworkEvent = () => {
    setNetworkEvents([...networkEvents, { eventSource: '' }]);
  };

  const removeNetworkEvent = (index) => {
    setNetworkEvents(networkEvents.filter((_, i) => i !== index));
  };

  const updateNetworkEvent = (index, field, value) => {
    const updated = [...networkEvents];
    updated[index] = { ...updated[index], [field]: value };
    setNetworkEvents(updated);
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return trailName.trim() !== '';
      case 1:
        return selectedAccounts.length > 0;
      case 2:
        return s3BucketAccountId !== '' && s3BucketName.trim() !== '';
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    const eventConfig = {};

    if (mgmtEnabled) {
      eventConfig.managementEvents = {
        enabled: true,
        readWriteType: mgmtReadWriteType,
        excludeKMS,
        excludeRDSData,
      };
    }

    if (dataEvents.length > 0) {
      eventConfig.dataEvents = dataEvents.map((de) => ({
        resourceType: de.resourceType,
        readWriteType: de.readWriteType,
        resourceArns: de.resourceArns
          ? de.resourceArns.split(',').map((s) => s.trim()).filter(Boolean).map((arn) =>
              de.resourceType === 'AWS::S3::Object' && !arn.endsWith('/') ? arn + '/' : arn
            )
          : [],
      }));
    }

    const insightSelectors = [];
    if (insightApiCall) insightSelectors.push({ insightType: 'ApiCallRateInsight' });
    if (insightApiError) insightSelectors.push({ insightType: 'ApiErrorRateInsight' });
    if (insightSelectors.length > 0) {
      eventConfig.insightSelectors = insightSelectors;
    }

    if (networkEvents.length > 0) {
      eventConfig.networkActivityEvents = networkEvents.map((ne) => ({
        eventSource: ne.eventSource,
      }));
    }

    try {
      const result = await createTrail({
        name: trailName,
        targetAccountIds: selectedAccounts,
        s3BucketAccountId,
        s3BucketName,
        s3BucketIsNew: s3BucketOption === 'new',
        s3Prefix,
        isMultiRegion,
        region,
        eventConfig,
      });

      toast.success('Trail creation started — tracking progress');
      navigate(`/trails/${result.data?.id}`);
    } catch (err) {
      setError(err.message);
      toast.error(`Failed to create trail: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#4698D3' }} />
      </Box>
    );
  }

  const sectionSx = {
    fontFamily: 'Metropolis, sans-serif',
    fontWeight: 600,
    color: '#253E66',
    mb: 2,
    fontSize: '1rem',
  };

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography sx={sectionSx}>Trail Settings</Typography>
            <TextField
              label="Trail Name"
              value={trailName}
              onChange={(e) => setTrailName(e.target.value)}
              fullWidth
              required
              sx={{ mb: 3 }}
              helperText="A unique name for this trail within your organization"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={isMultiRegion}
                  onChange={(e) => setIsMultiRegion(e.target.checked)}
                  color="primary"
                />
              }
              label="Multi-region trail"
            />
            <Typography variant="body2" sx={{ color: '#666', ml: 6, mt: -0.5, mb: 3 }}>
              Log events from all AWS regions (recommended)
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Trail Region</InputLabel>
              <Select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                label="Trail Region"
              >
                <MenuItem value="us-east-1">US East (N. Virginia) — us-east-1</MenuItem>
                <MenuItem value="us-east-2">US East (Ohio) — us-east-2</MenuItem>
                <MenuItem value="us-west-1">US West (N. California) — us-west-1</MenuItem>
                <MenuItem value="us-west-2">US West (Oregon) — us-west-2</MenuItem>
                <MenuItem value="ap-south-1">Asia Pacific (Mumbai) — ap-south-1</MenuItem>
                <MenuItem value="ap-northeast-3">Asia Pacific (Osaka) — ap-northeast-3</MenuItem>
                <MenuItem value="ap-northeast-2">Asia Pacific (Seoul) — ap-northeast-2</MenuItem>
                <MenuItem value="ap-southeast-1">Asia Pacific (Singapore) — ap-southeast-1</MenuItem>
                <MenuItem value="ap-southeast-2">Asia Pacific (Sydney) — ap-southeast-2</MenuItem>
                <MenuItem value="ap-northeast-1">Asia Pacific (Tokyo) — ap-northeast-1</MenuItem>
                <MenuItem value="ca-central-1">Canada (Central) — ca-central-1</MenuItem>
                <MenuItem value="eu-central-1">Europe (Frankfurt) — eu-central-1</MenuItem>
                <MenuItem value="eu-west-1">Europe (Ireland) — eu-west-1</MenuItem>
                <MenuItem value="eu-west-2">Europe (London) — eu-west-2</MenuItem>
                <MenuItem value="eu-west-3">Europe (Paris) — eu-west-3</MenuItem>
                <MenuItem value="eu-north-1">Europe (Stockholm) — eu-north-1</MenuItem>
                <MenuItem value="sa-east-1">South America (Sao Paulo) — sa-east-1</MenuItem>
              </Select>
              <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                The AWS region where the trail will be created
              </Typography>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography sx={sectionSx}>Select Accounts</Typography>
              <Button size="small" onClick={handleSelectAll} sx={{ textTransform: 'none' }}>
                {selectedAccounts.length === accounts.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Selected: {selectedAccounts.length} of {accounts.length} accounts
            </Typography>
            <TextField
              placeholder="Search accounts..."
              value={accountSearch}
              onChange={(e) => setAccountSearch(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #E5E5E5', borderRadius: '6px' }}>
              {accounts.filter((account) => {
                if (!accountSearch.trim()) return true;
                const query = accountSearch.toLowerCase().trim();
                return account.name.toLowerCase().includes(query) || account.accountId.includes(query);
              }).map((account) => (
                <Box
                  key={account.accountId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    borderBottom: '1px solid #F0F0F0',
                    '&:hover': { backgroundColor: '#F9FBFD' },
                    cursor: 'pointer',
                  }}
                  onClick={() => handleToggleAccount(account.accountId)}
                >
                  <Checkbox
                    checked={selectedAccounts.includes(account.accountId)}
                    sx={{ mr: 1 }}
                  />
                  <Box>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontWeight: 600, color: '#253E66', fontSize: '0.875rem' }}>
                      {account.name}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Metropolis, sans-serif', color: '#999', fontSize: '0.75rem' }}>
                      {account.accountId}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography sx={sectionSx}>S3 Bucket Configuration</Typography>

            <Autocomplete
              options={accounts.filter((a) => selectedAccounts.includes(a.accountId))}
              getOptionLabel={(option) => `${option.name} (${option.accountId})`}
              value={accounts.find((a) => a.accountId === s3BucketAccountId) || null}
              onChange={(_, newValue) => setS3BucketAccountId(newValue ? newValue.accountId : '')}
              isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
              renderInput={(params) => (
                <TextField {...params} label="Bucket Account" />
              )}
              fullWidth
              sx={{ mb: 3 }}
            />

            <RadioGroup
              value={s3BucketOption}
              onChange={(e) => setS3BucketOption(e.target.value)}
              sx={{ mb: 2 }}
            >
              <FormControlLabel value="new" control={<Radio />} label="Create new bucket" />
              <FormControlLabel value="existing" control={<Radio />} label="Use existing bucket" />
            </RadioGroup>

            <TextField
              label="S3 Bucket Name"
              value={s3BucketName}
              onChange={(e) => setS3BucketName(e.target.value)}
              fullWidth
              required
              sx={{ mb: 3 }}
              helperText={
                s3BucketOption === 'new'
                  ? 'A new bucket will be created with this name'
                  : 'Enter the name of an existing S3 bucket'
              }
            />

            <TextField
              label="S3 Key Prefix (optional)"
              value={s3Prefix}
              onChange={(e) => setS3Prefix(e.target.value)}
              fullWidth
              helperText="Optional prefix for CloudTrail log files in the bucket"
            />
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography sx={sectionSx}>Event Configuration</Typography>

            {/* Management Events */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #E5E5E5', borderRadius: '6px' }}>
              <FormControlLabel
                control={
                  <Switch checked={mgmtEnabled} onChange={(e) => setMgmtEnabled(e.target.checked)} />
                }
                label={<Typography sx={{ fontWeight: 600 }}>Management Events</Typography>}
              />
              {mgmtEnabled && (
                <Box sx={{ ml: 4, mt: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 200, mb: 1 }}>
                    <InputLabel>Read/Write Type</InputLabel>
                    <Select
                      value={mgmtReadWriteType}
                      onChange={(e) => setMgmtReadWriteType(e.target.value)}
                      label="Read/Write Type"
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="ReadOnly">Read Only</MenuItem>
                      <MenuItem value="WriteOnly">Write Only</MenuItem>
                    </Select>
                  </FormControl>
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox checked={excludeKMS} onChange={(e) => setExcludeKMS(e.target.checked)} size="small" />}
                      label="Exclude AWS KMS events"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={excludeRDSData} onChange={(e) => setExcludeRDSData(e.target.checked)} size="small" />}
                      label="Exclude Amazon RDS Data API events"
                    />
                  </FormGroup>
                </Box>
              )}
            </Paper>

            {/* Data Events */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #E5E5E5', borderRadius: '6px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>Data Events</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addDataEvent} sx={{ textTransform: 'none' }}>
                  Add
                </Button>
              </Box>
              {dataEvents.length === 0 && (
                <Typography variant="body2" sx={{ color: '#999' }}>No data event selectors configured</Typography>
              )}
              {dataEvents.map((de, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap' }}>
                  <Autocomplete
                    options={dataResourceTypes}
                    value={de.resourceType}
                    onChange={(_, newValue) => updateDataEvent(idx, 'resourceType', newValue || 'AWS::S3::Object')}
                    size="small"
                    sx={{ minWidth: 260 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Resource Type" placeholder="Search resource types..." />
                    )}
                  />
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Read/Write</InputLabel>
                    <Select
                      value={de.readWriteType}
                      onChange={(e) => updateDataEvent(idx, 'readWriteType', e.target.value)}
                      label="Read/Write"
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="ReadOnly">Read Only</MenuItem>
                      <MenuItem value="WriteOnly">Write Only</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Resource ARNs (comma-separated, optional)"
                    value={de.resourceArns}
                    onChange={(e) => updateDataEvent(idx, 'resourceArns', e.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                  <IconButton size="small" onClick={() => removeDataEvent(idx)} sx={{ color: '#E74C3C', mt: 0.5 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Paper>

            {/* Insights */}
            <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid #E5E5E5', borderRadius: '6px' }}>
              <Typography sx={{ fontWeight: 600, mb: 1 }}>Insight Events</Typography>
              <FormGroup>
                <FormControlLabel
                  control={<Checkbox checked={insightApiCall} onChange={(e) => setInsightApiCall(e.target.checked)} size="small" />}
                  label="API call rate insight"
                />
                <FormControlLabel
                  control={<Checkbox checked={insightApiError} onChange={(e) => setInsightApiError(e.target.checked)} size="small" />}
                  label="API error rate insight"
                />
              </FormGroup>
            </Paper>

            {/* Network Activity */}
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #E5E5E5', borderRadius: '6px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>Network Activity Events</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => {
                      const existing = new Set(networkEvents.map((ne) => ne.eventSource));
                      const toAdd = networkEventSources.filter((src) => !existing.has(src));
                      if (toAdd.length > 0) {
                        setNetworkEvents([...networkEvents, ...toAdd.map((src) => ({ eventSource: src }))]);
                      }
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Select All
                  </Button>
                  <Button size="small" startIcon={<AddIcon />} onClick={addNetworkEvent} sx={{ textTransform: 'none' }}>
                    Add
                  </Button>
                </Box>
              </Box>
              {networkEvents.length === 0 && (
                <Typography variant="body2" sx={{ color: '#999' }}>No network activity event selectors configured</Typography>
              )}
              {networkEvents.map((ne, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
                  <Autocomplete
                    freeSolo
                    options={networkEventSources}
                    value={ne.eventSource || ''}
                    onChange={(_, newValue) => updateNetworkEvent(idx, 'eventSource', newValue || '')}
                    onInputChange={(_, newInput, reason) => {
                      if (reason === 'input') updateNetworkEvent(idx, 'eventSource', newInput);
                    }}
                    size="small"
                    sx={{ minWidth: 300, flex: 1 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Event Source" placeholder="e.g. ec2.amazonaws.com" />
                    )}
                  />
                  <IconButton size="small" onClick={() => removeNetworkEvent(idx)} sx={{ color: '#E74C3C' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  // Review panel (shown on step 4 before submit)
  const renderReview = () => {
    if (activeStep !== 3) return null;

    return (
      <Paper elevation={0} sx={{ p: 2, mt: 3, border: '1px solid #E5E5E5', borderRadius: '6px', backgroundColor: '#F9FBFD' }}>
        <Typography sx={{ fontWeight: 600, color: '#253E66', mb: 1.5, fontFamily: 'Metropolis, sans-serif' }}>
          Review
        </Typography>
        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.875rem' }}>
          <Typography variant="body2" sx={{ color: '#666' }}>Trail Name:</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{trailName}</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Region:</Typography>
          <Typography variant="body2">{region}</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Multi-Region:</Typography>
          <Typography variant="body2">{isMultiRegion ? 'Yes' : 'No'}</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Accounts:</Typography>
          <Typography variant="body2">{selectedAccounts.length} selected</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>S3 Bucket:</Typography>
          <Typography variant="body2">{s3BucketName} ({s3BucketOption === 'new' ? 'new' : 'existing'})</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>Bucket Account:</Typography>
          <Typography variant="body2">{s3BucketAccountId}</Typography>
          {s3Prefix && (
            <>
              <Typography variant="body2" sx={{ color: '#666' }}>S3 Prefix:</Typography>
              <Typography variant="body2">{s3Prefix}</Typography>
            </>
          )}
        </Box>
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="body2" sx={{ color: '#666' }}>Events:</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            {mgmtEnabled && <Chip label="Management" size="small" />}
            {dataEvents.length > 0 && <Chip label={`${dataEvents.length} Data`} size="small" />}
            {(insightApiCall || insightApiError) && <Chip label="Insights" size="small" />}
            {networkEvents.length > 0 && <Chip label={`${networkEvents.length} Network`} size="small" />}
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{
            textTransform: 'none',
            color: '#666',
            fontFamily: 'Metropolis, sans-serif',
            mb: 2,
          }}
        >
          Back to Trails
        </Button>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Bitter, Georgia, serif',
            fontWeight: 600,
            color: '#253E66',
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
          }}
        >
          {cloneFromId ? 'Clone Trail' : 'Create Trail'}
        </Typography>
      </Box>
      </motion.div>

      <motion.div variants={sectionVariants}>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconProps={{
                sx: {
                  '&.Mui-active': { color: '#4698D3' },
                  '&.Mui-completed': { color: '#4698D3' },
                },
              }}
            >
              <Typography sx={{ fontFamily: 'Metropolis, sans-serif', fontSize: '0.875rem' }}>
                {label}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, border: '1px solid #E5E5E5', borderRadius: '8px' }}>
        {renderStep()}
        {renderReview()}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid #E5E5E5' }}>
          <Button
            disabled={activeStep === 0}
            onClick={() => setActiveStep((prev) => prev - 1)}
            sx={{ textTransform: 'none', fontFamily: 'Metropolis, sans-serif' }}
          >
            Back
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !canProceed()}
              sx={{
                backgroundColor: '#4698D3',
                textTransform: 'none',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                borderRadius: '6px',
                px: 4,
                '&:hover': { backgroundColor: '#3A7BB0' },
              }}
            >
              {submitting ? (
                <>
                  <CircularProgress size={20} sx={{ color: '#fff', mr: 1 }} />
                  Creating...
                </>
              ) : (
                'Create Trail'
              )}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setActiveStep((prev) => prev + 1)}
              disabled={!canProceed()}
              sx={{
                backgroundColor: '#4698D3',
                textTransform: 'none',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                borderRadius: '6px',
                px: 3,
                '&:hover': { backgroundColor: '#3A7BB0' },
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>
      </motion.div>
    </motion.div>
  );
};

export default CreateTrail;
